import { createWithEqualityFn as create } from "zustand/traditional";
import { IPaginationQuery } from "../../Typings/MessageText";
import { createPaginationQueryString } from "../../Queries/queryHelpers";

export interface ISelectedConflict {
  conflictId: string;
  rowNumber: number;
}

interface IImportTabStoreValues {
  tabValue: number;
  includeResolved: boolean;
  selectAll: boolean;
  selectedConflicts: string[];
  query: Required<IPaginationQuery>;
}

interface IImportTabStoreActions {
  setTabValue: (value: number) => void;
  setDisplayPendingOnly: (value: boolean) => void;
  setSelectAll: (value: boolean) => void;
  setSelectedConflicts: (conflicts: string[]) => void;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  addSelectedConflict: (conflict: string) => void;
  addSelectedConflicts: (conflicts: string[]) => void;
  isSelected: (conflict: string) => boolean;
  removeSelectedConflict: (conflict: string) => void;
  toQueryString: (logType?: string) => string;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangePage: (_: unknown, newPage: number) => void;
  handleChangePageSize: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (property: string) => void;
  reset: () => void;
  resetPagination: () => void;
}

type ImportTabStore = IImportTabStoreValues & IImportTabStoreActions;

export const useImportTabStore = create<ImportTabStore>((set, get) => ({
  tabValue: 0,
  includeResolved: false,
  selectAll: false,
  selectedConflicts: [],
  query: {
    search: "",
    page: 0,
    pageSize: 10,
    sortBy: "rowNumber",
    sortDirection: "asc",
  },
  setTabValue: (value) => set({ tabValue: value }),
  setDisplayPendingOnly: (value) => set({ includeResolved: value }),
  setSelectAll: (value) => set({ selectAll: value }),
  setPage: (page) => set({ query: { ...get().query, page } }),
  setSearch: (search) => set({ query: { ...get().query, search, page: 0 } }),
  setSelectedConflicts: (conflicts) => set({ selectedConflicts: conflicts }),
  isSelected: (conflictId) => get().selectedConflicts.find((c) => c === conflictId) !== undefined,
  addSelectedConflict: (conflictId) => {
    const selected = new Set(get().selectedConflicts);
    selected.add(conflictId);
    set({ selectedConflicts: Array.from(selected) });
  },
  addSelectedConflicts: (conflicts) => {
    const selected = new Set(get().selectedConflicts);
    conflicts.forEach((c) => selected.add(c));
    set({ selectedConflicts: Array.from(selected) });
  },
  removeSelectedConflict: (conflictId) =>
    set({ selectedConflicts: get().selectedConflicts.filter((c) => c !== conflictId) }),
  handleChangePage: (_, newPage) => set({ query: { ...get().query, page: newPage } }),
  handleChangePageSize: (event) =>
    set({ query: { ...get().query, pageSize: parseInt(event.target.value, 10), page: 0 } }),
  handleRequestSort: (property) => {
    const query = get().query;
    const isAsc = query.sortBy === property && query.sortDirection === "asc";
    set({ query: { ...query, sortBy: property, sortDirection: isAsc ? "desc" : "asc", page: 0 } });
  },
  handleSearchChange: (event) => set({ query: { ...get().query, search: event.target.value, page: 0 } }),
  toQueryString: (logType = "changes") =>
    logType === "conflicts"
      ? `${createPaginationQueryString({ ...get().query, page: get().query.page + 1 })}&includeResolved=${get().includeResolved}&selectAll=${get().selectAll}`
      : `${createPaginationQueryString({ ...get().query, page: get().query.page + 1 })}`,
  reset: () =>
    set({
      tabValue: 0,
      selectedConflicts: [],
      selectAll: false,
      includeResolved: false,
    }),
  resetPagination: () =>
    set({
      query: {
        page: 0,
        pageSize: 10,
        sortBy: "rowNumber",
        sortDirection: "asc",
        search: "",
      },
    }),
}));
